/**
 * Scheduled Runner Service
 *
 * Automatically creates transactions from scheduled items on their due dates.
 *
 * Threshold logic:
 * - Due TODAY or YESTERDAY: Auto-create silently (appears in transaction list)
 * - Due 2+ days ago: Goes to Overdue section for user review (not auto-created)
 *
 * This runs on:
 * - App startup (after login)
 * - Tab focus/visibility change
 * - After sync completes
 * - Every 4 hours if app is open
 */

import { db, Transaction, ScheduledTransaction } from './db';
import { transactionService } from './transactions';
import {
  scheduledTransactionService,
  skippedOccurrenceService,
} from './scheduledTransactions';
import { dataEvents } from './dataEvents';
import { setBridgeCooldown } from './scheduledBridge';

// ============================================================
// Debug
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[RUNNER]', ...args);
  }
}

// ============================================================
// Date Helpers
// ============================================================

/**
 * Get start of day (midnight) for a date.
 */
function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Subtract days from a date.
 */
function subDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
}

/**
 * Check if two dates are the same day.
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// ============================================================
// Runner State
// ============================================================

let isRunning = false;
let lastRunTime: Date | null = null;
let intervalId: number | null = null;

// Minimum time between runs (prevent rapid re-runs)
const MIN_RUN_INTERVAL_MS = 60 * 1000; // 1 minute

// Auto-run interval
const AUTO_RUN_INTERVAL_MS = 4 * 60 * 60 * 1000; // 4 hours

// Maximum days to show in overdue list
// Items older than this are automatically advanced to future date
// (User clearly isn't tracking these - don't show stale entries)
const MAX_OVERDUE_DAYS = 60;

// ============================================================
// Core Runner Logic
// ============================================================

/**
 * Convert a scheduled transaction to a regular transaction.
 * BUG-073 FIX: Fetches account currency instead of hardcoding USD.
 */
async function scheduleToTransaction(
  schedule: ScheduledTransaction,
  dueDate: Date
): Promise<Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'syncStatus'>> {
  // BUG-073 FIX: Get currency from account (was hardcoded 'USD')
  let currency = 'USD';
  try {
    const account = await db.accounts.get(schedule.accountId);
    if (account?.currency) {
      currency = account.currency;
    }
  } catch (err) {
    log('[WARN] Failed to get account currency, using USD fallback');
  }

  return {
    accountId: schedule.accountId,
    type: schedule.type,
    amount: schedule.amount,
    categoryId: schedule.categoryId,
    toAccountId: schedule.toAccountId,
    description: schedule.description,
    date: dueDate,
    currency,
    tags: ['scheduled', `schedule:${schedule.id}`],
    scheduledTransactionId: schedule.id,
  };
}

/**
 * Check if a transaction was already created for this schedule and date.
 * Prevents duplicate transactions.
 */
async function wasAlreadyCreated(
  scheduleId: string,
  dueDate: Date
): Promise<boolean> {
  try {
    const targetDate = startOfDay(dueDate);

    // Check for existing transaction with this schedule ID and date
    const existing = await db.transactions
      .where('scheduledTransactionId')
      .equals(scheduleId)
      .filter((t) => {
        if (t.deletedAt) return false;
        return isSameDay(new Date(t.date), targetDate);
      })
      .first();

    return !!existing;
  } catch (err) {
    // Handle missing index gracefully - fall back to full scan
    if (err instanceof Error && err.name === 'NotFoundError') {
      console.warn('[RUNNER] scheduledTransactionId index not found, falling back');
      try {
        const all = await db.transactions.toArray();
        const targetDate = startOfDay(dueDate);
        return all.some((t) => {
          if (t.deletedAt) return false;
          if (t.scheduledTransactionId !== scheduleId) return false;
          return isSameDay(new Date(t.date), targetDate);
        });
      } catch {
        return false;
      }
    }
    throw err;
  }
}

/**
 * Check if this occurrence was skipped.
 */
async function wasSkipped(scheduleId: string, dueDate: Date): Promise<boolean> {
  return skippedOccurrenceService.wasSkipped(scheduleId, dueDate);
}

/**
 * Run the scheduled transaction creation process.
 *
 * @returns Number of transactions created
 */
export async function runScheduledCreation(): Promise<{
  created: number;
  skipped: number;
  overdueCount: number;
}> {
  // Prevent concurrent runs
  if (isRunning) {
    log('[SKIP] Already running');
    return { created: 0, skipped: 0, overdueCount: 0 };
  }

  // Throttle runs
  if (lastRunTime) {
    const elapsed = Date.now() - lastRunTime.getTime();
    if (elapsed < MIN_RUN_INTERVAL_MS) {
      log('[SKIP] Throttled, last run was', Math.round(elapsed / 1000), 'seconds ago');
      return { created: 0, skipped: 0, overdueCount: 0 };
    }
  }

  isRunning = true;
  lastRunTime = new Date();
  log('[START] Running scheduled transaction creation');

  let created = 0;
  let skippedCount = 0;
  let overdueCount = 0;

  try {
    // Auto-resume any paused schedules whose pausedUntil has passed
    const resumed = await scheduledTransactionService.autoResumeExpiredPauses();
    if (resumed > 0) {
      log('[OK] Auto-resumed', resumed, 'expired pauses');
    }

    const today = startOfDay(new Date());
    const yesterday = subDays(today, 1);
    const twoDaysAgo = subDays(today, 2);
    const maxOverdueDate = subDays(today, MAX_OVERDUE_DAYS);

    // Get all active schedules
    const schedules = await scheduledTransactionService.getActive();
    console.log('[SCHEDULED-RUNNER] Found', schedules.length, 'active schedules');

    let staleAutoAdvanced = 0;

    // BUG-033 FIX: Track seen detectionKeys to prevent processing duplicates
    const seenDetectionKeys = new Set<string>();

    for (const schedule of schedules) {
      // Dedupe by detectionKey - only process first occurrence
      if (schedule.detectionKey) {
        const normalizedKey = schedule.detectionKey.toLowerCase().trim();
        if (seenDetectionKeys.has(normalizedKey)) {
          log('[DEDUPE] Skipping duplicate schedule:', schedule.description);
          continue;
        }
        seenDetectionKeys.add(normalizedKey);
      }
      const dueDate = startOfDay(schedule.nextDueDate);

      // Skip future due dates
      if (dueDate > today) {
        continue;
      }

      // Check if already handled
      if (await wasAlreadyCreated(schedule.id, dueDate)) {
        log('[SKIP] Already created:', schedule.description, 'for', dueDate);
        // Advance to next due date since this one is handled
        await scheduledTransactionService.advanceToNextDueDate(schedule.id);
        continue;
      }

      // Check if skipped
      if (await wasSkipped(schedule.id, dueDate)) {
        log('[SKIP] Was skipped:', schedule.description, 'for', dueDate);
        skippedCount++;
        // Advance to next due date
        await scheduledTransactionService.advanceToNextDueDate(schedule.id);
        continue;
      }

      // Handle VERY stale items (> MAX_OVERDUE_DAYS)
      // Auto-advance these to future date - user clearly isn't tracking them
      if (dueDate < maxOverdueDate) {
        log('[STALE] Auto-advancing very old item:', schedule.description, 'was due', dueDate);
        await scheduledTransactionService.advanceToNextDueDate(schedule.id);
        staleAutoAdvanced++;
        continue;
      }

      // Determine action based on how overdue
      if (dueDate >= yesterday) {
        // Due today or yesterday: Auto-create silently
        const txData = await scheduleToTransaction(schedule, dueDate);
        await transactionService.create(txData);
        created++;
        log('[OK] Created transaction:', schedule.description, 'for', dueDate);

        // Advance schedule to next due date
        await scheduledTransactionService.advanceToNextDueDate(schedule.id);
      } else if (dueDate >= twoDaysAgo) {
        // 2 days ago: borderline - count as overdue but could auto-create
        // For now, mark as overdue for user review
        overdueCount++;
        log('[OVERDUE] 2 days ago:', schedule.description, 'for', dueDate);
      } else {
        // 3-60 days ago: Overdue - needs user review
        overdueCount++;
        log('[OVERDUE] Old:', schedule.description, 'for', dueDate);
      }
    }

    if (staleAutoAdvanced > 0) {
      console.log('[SCHEDULED-RUNNER] Auto-advanced', staleAutoAdvanced, 'stale items (>' + MAX_OVERDUE_DAYS + ' days old)');
    }

    console.log('[SCHEDULED-RUNNER] Result: created', created, ', skipped', skippedCount, ', overdue', overdueCount);

    // Notify UI if we created transactions
    if (created > 0) {
      dataEvents.emit('transactions');
      dataEvents.emit('accounts');
    }

    return { created, skipped: skippedCount, overdueCount };
  } catch (err) {
    console.error('[RUNNER] Error:', err);
    return { created: 0, skipped: 0, overdueCount: 0 };
  } finally {
    isRunning = false;
  }
}

/**
 * Get overdue scheduled transactions with their details.
 * These are items that need user action (Pay, Skip, Ignore).
 */
export interface OverdueItem {
  schedule: ScheduledTransaction;
  dueDate: Date;
  daysOverdue: number;
}

export async function getOverdueItems(): Promise<OverdueItem[]> {
  const today = startOfDay(new Date());
  const twoDaysAgo = subDays(today, 2);
  const maxOverdueDate = subDays(today, MAX_OVERDUE_DAYS);

  const schedules = await scheduledTransactionService.getActive();
  const overdueItems: OverdueItem[] = [];

  // BUG-033 FIX: Track seen detectionKeys to prevent duplicates
  const seenDetectionKeys = new Set<string>();

  for (const schedule of schedules) {
    // Dedupe by detectionKey - only show first occurrence (skip duplicates)
    if (schedule.detectionKey) {
      const normalizedKey = schedule.detectionKey.toLowerCase().trim();
      if (seenDetectionKeys.has(normalizedKey)) {
        log('[DEDUPE] Skipping duplicate:', schedule.description, 'key:', normalizedKey);
        continue;
      }
      seenDetectionKeys.add(normalizedKey);
    }

    const dueDate = startOfDay(schedule.nextDueDate);

    // Only include items due 2+ days ago
    if (dueDate > twoDaysAgo) continue;

    // Skip items that are TOO old (> MAX_OVERDUE_DAYS)
    // These should be auto-advanced by runScheduledCreation() instead
    if (dueDate < maxOverdueDate) {
      log('[STALE] Skipping very old item:', schedule.description, 'due', dueDate, `(${MAX_OVERDUE_DAYS}+ days old)`);
      continue;
    }

    // Check if already handled
    if (await wasAlreadyCreated(schedule.id, dueDate)) continue;
    if (await wasSkipped(schedule.id, dueDate)) continue;

    const daysOverdue = Math.floor(
      (today.getTime() - dueDate.getTime()) / (24 * 60 * 60 * 1000)
    );

    overdueItems.push({
      schedule,
      dueDate,
      daysOverdue,
    });
  }

  // Sort by most overdue first
  overdueItems.sort((a, b) => b.daysOverdue - a.daysOverdue);

  return overdueItems;
}

/**
 * Handle "Pay" action from Overdue section.
 * Creates the transaction for the overdue date.
 */
export async function handleOverduePay(
  scheduleId: string,
  dueDate: Date
): Promise<string> {
  const schedule = await scheduledTransactionService.getById(scheduleId);
  if (!schedule) {
    throw new Error('Schedule not found');
  }

  // Check for duplicate
  if (await wasAlreadyCreated(scheduleId, dueDate)) {
    throw new Error('Transaction already exists for this date');
  }

  // Create the transaction
  const txData = await scheduleToTransaction(schedule, dueDate);
  const txId = await transactionService.create(txData);

  // Advance schedule to next due date
  await scheduledTransactionService.advanceToNextDueDate(scheduleId);

  log('[OK] Paid overdue:', schedule.description, 'for', dueDate, 'txId:', txId);

  return txId;
}

/**
 * Handle "Skip this time" action from Overdue section.
 */
export async function handleOverdueSkip(
  scheduleId: string,
  dueDate: Date,
  reason?: string
): Promise<void> {
  // Create skip record
  await skippedOccurrenceService.create(scheduleId, dueDate, reason);

  // Advance schedule to next due date
  await scheduledTransactionService.advanceToNextDueDate(scheduleId);

  const schedule = await scheduledTransactionService.getById(scheduleId);
  log('[OK] Skipped overdue:', schedule?.description, 'for', dueDate);

  dataEvents.emit('all');
}

/**
 * Handle "Skip & Stop Recurring" action from Overdue section.
 * Skips this occurrence AND deletes the schedule entirely.
 * User can still re-create recurring by adding new transaction with same name.
 *
 * BUG-053 FIX: Set isIgnoredPermanently BEFORE soft-deleting.
 * This ensures the bridge won't recreate the schedule even if sync
 * hard-deletes the record later. Belt-and-suspenders protection.
 *
 * RECURRING-FIX: Issue 5B - Sets bridge cooldown to prevent race recreation.
 */
export async function handleOverdueSkipAndRemove(
  scheduleId: string,
  dueDate: Date
): Promise<void> {
  const schedule = await scheduledTransactionService.getById(scheduleId);

  // Create skip record for this date (for history)
  await skippedOccurrenceService.create(scheduleId, dueDate, 'Stopped recurring');

  // BUG-053 FIX: Mark as permanently ignored BEFORE deleting
  // This ensures findByDetectionKey() sees isIgnoredPermanently=true
  // even if the record somehow gets hard-deleted later
  await scheduledTransactionService.ignorePermanently(scheduleId);

  // Delete the schedule (soft delete - preserves record for detection blocking)
  await scheduledTransactionService.delete(scheduleId);

  // RECURRING-FIX: Issue 5B - Set cooldown to prevent bridge from recreating
  // while sync propagates to other devices
  setBridgeCooldown(15);

  log('[OK] Skip & removed recurring:', schedule?.description);

  dataEvents.emit('all');
}

/**
 * Handle "Ignore 1 month" action from Overdue section.
 */
export async function handleOverdueIgnoreMonth(scheduleId: string): Promise<void> {
  const pausedUntil = new Date();
  pausedUntil.setMonth(pausedUntil.getMonth() + 1);

  await scheduledTransactionService.pause(scheduleId, pausedUntil);

  const schedule = await scheduledTransactionService.getById(scheduleId);
  log('[OK] Ignored for 1 month:', schedule?.description, 'until', pausedUntil);

  dataEvents.emit('all');
}

/**
 * Handle "Ignore permanently" action from Overdue section.
 *
 * RECURRING-FIX: Issue 5B - Sets bridge cooldown to prevent race recreation.
 */
export async function handleOverdueIgnorePermanently(
  scheduleId: string
): Promise<void> {
  await scheduledTransactionService.ignorePermanently(scheduleId);

  // RECURRING-FIX: Issue 5B - Set cooldown to prevent bridge from recreating
  setBridgeCooldown(15);

  const schedule = await scheduledTransactionService.getById(scheduleId);
  log('[OK] Ignored permanently:', schedule?.description);

  dataEvents.emit('all');
}

/**
 * Handle "Dismiss" action from Overdue section.
 * First dismiss just pauses (can re-detect later).
 * Second+ dismiss returns showEscalationPopup: true.
 */
export async function handleOverdueDismiss(
  scheduleId: string
): Promise<{ showEscalationPopup: boolean }> {
  const result = await scheduledTransactionService.dismiss(scheduleId);

  const schedule = await scheduledTransactionService.getById(scheduleId);
  log('[OK] Dismissed:', schedule?.description, 'showEscalation:', result.showEscalationPopup);

  dataEvents.emit('all');
  return result;
}

// ============================================================
// Runner Lifecycle
// ============================================================

/**
 * Start the scheduled runner.
 * Call this after user logs in.
 */
export function startScheduledRunner(): void {
  console.log('[SCHEDULED-RUNNER] Starting...');

  // Run immediately
  runScheduledCreation();

  // Set up periodic runs
  if (intervalId) {
    clearInterval(intervalId);
  }
  intervalId = window.setInterval(runScheduledCreation, AUTO_RUN_INTERVAL_MS);

  // Run on tab focus
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      log('[TRIGGER] Tab became visible');
      runScheduledCreation();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Store cleanup function
  (window as unknown as { __scheduledRunnerCleanup?: () => void }).__scheduledRunnerCleanup = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * Stop the scheduled runner.
 * Call this on user logout.
 */
export function stopScheduledRunner(): void {
  log('[STOP] Stopping scheduled runner');

  const cleanup = (window as unknown as { __scheduledRunnerCleanup?: () => void }).__scheduledRunnerCleanup;
  if (cleanup) {
    cleanup();
    delete (window as unknown as { __scheduledRunnerCleanup?: () => void }).__scheduledRunnerCleanup;
  }
}

/**
 * Trigger a manual run (e.g., after sync).
 */
export function triggerScheduledRun(): void {
  log('[TRIGGER] Manual run requested');
  runScheduledCreation();
}
